import { useCallback, useEffect, useState } from 'react';
import {
  Page,
  Card,
  FormLayout,
  TextField,
  BlockStack,
  InlineStack,
  Text,
  Button,
  Checkbox,
  Banner,
  RangeSlider,
  ButtonGroup,
  Select,
} from '@shopify/polaris';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApi } from '../../providers/ApiProvider';
import {
  DEFAULT_CONGRATS_DESIGN,
  toColorInputValue,
  type CongratsDesign,
} from './congratsDefaults';
import { CongratsPreview } from './CongratsPreview';
import {
  ContextualSaveBar,
  SavingOverlay,
  isShopifyEmbedded,
  showAppToast,
  useUnsavedChanges,
} from '../../components/ContextualSaveBar';
import { ThemeColorRefresh } from '../../components/ThemeColorRefresh';
import { t } from '../../i18n';

function ColorField({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  const pickerValue = toColorInputValue(value, '#000000');
  return (
    <div>
      <Text as="p" variant="bodyMd">
        {label}
      </Text>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginTop: 6,
        }}
      >
        <input
          type="color"
          value={pickerValue}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          aria-label={label}
          style={{
            width: 40,
            height: 36,
            padding: 0,
            border: '1px solid #c9cccf',
            borderRadius: 8,
            background: 'transparent',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.5 : 1,
          }}
        />
        <div style={{ flex: 1 }}>
          <TextField
            label={label}
            labelHidden
            value={value}
            onChange={onChange}
            autoComplete="off"
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  );
}

export function CongratsEditorPage() {
  const api = useApi();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('return') || '/widgets';

  const [design, setDesign] = useState<CongratsDesign>({
    ...DEFAULT_CONGRATS_DESIGN,
  });
  const [widgetId, setWidgetId] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>(
    'desktop',
  );
  const [previewProgress, setPreviewProgress] = useState(100);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const embedded = isShopifyEmbedded();
  const { dirty, markClean, getBaseline, markCleanAfterDiscard } =
    useUnsavedChanges(design);

  const patch = useCallback(
    <K extends keyof CongratsDesign>(key: K, value: CongratsDesign[K]) => {
      setDesign((d) => ({ ...d, [key]: value }));
    },
    [],
  );

  // Animate preview progress bar to show auto-close border
  useEffect(() => {
    if (!design.showCloseProgress) {
      setPreviewProgress(100);
      return;
    }
    setPreviewProgress(100);
    const start = Date.now();
    const duration = Math.max(2000, Math.min(design.autoCloseMs, 6000));
    const id = window.setInterval(() => {
      const elapsed = Date.now() - start;
      const left = Math.max(0, 100 - (elapsed / duration) * 100);
      setPreviewProgress(left);
      if (left <= 0) {
        window.clearInterval(id);
        window.setTimeout(() => setPreviewProgress(100), 600);
      }
    }, 50);
    return () => window.clearInterval(id);
  }, [design.autoCloseMs, design.showCloseProgress, design.title, design.subtitle]);

  useEffect(() => {
    setLoading(true);
    api
      .get<
        Array<{
          id: string;
          type: string;
          content?: Record<string, unknown>;
          styles?: Record<string, unknown>;
          isEnabled?: boolean;
        }>
      >('/widgets')
      .then((list) => {
        const w = (list || []).find((x) => x.type === 'STICKY_BAR');
        if (!w) {
          const defaults = { ...DEFAULT_CONGRATS_DESIGN };
          setDesign(defaults);
          markClean(defaults);
          return;
        }
        setWidgetId(w.id);
        const c = w.content || {};
        const s = w.styles || {};
        const next: CongratsDesign = {
          ...DEFAULT_CONGRATS_DESIGN,
          title: String(c.title ?? DEFAULT_CONGRATS_DESIGN.title),
          subtitle: String(c.subtitle ?? DEFAULT_CONGRATS_DESIGN.subtitle),
          accentColor: String(s.accentColor ?? DEFAULT_CONGRATS_DESIGN.accentColor),
          backgroundColor: String(
            s.backgroundColor ?? DEFAULT_CONGRATS_DESIGN.backgroundColor,
          ),
          textColor: String(s.textColor ?? DEFAULT_CONGRATS_DESIGN.textColor),
          mutedColor: String(s.mutedColor ?? DEFAULT_CONGRATS_DESIGN.mutedColor),
          borderRadius: Number(
            s.borderRadius ?? DEFAULT_CONGRATS_DESIGN.borderRadius,
          ),
          autoCloseMs: Number(
            s.autoCloseMs ?? DEFAULT_CONGRATS_DESIGN.autoCloseMs,
          ),
          showCloseProgress: s.showCloseProgress !== false,
          position:
            s.position === 'top' || s.position === 'bottom'
              ? s.position
              : DEFAULT_CONGRATS_DESIGN.position,
          fontFamily: String(s.fontFamily ?? DEFAULT_CONGRATS_DESIGN.fontFamily),
          autoFitTheme: s.autoFitTheme !== false,
          themeSchemeId:
            typeof s.themeSchemeId === 'string' ? s.themeSchemeId : null,
          enabled: w.isEnabled !== false,
        };
        setDesign(next);
        markClean(next);
      })
      .catch(() => {
        const defaults = { ...DEFAULT_CONGRATS_DESIGN };
        setDesign(defaults);
        markClean(defaults);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  async function save() {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const payload = {
        name: 'Cart Success Notification Bar',
        type: 'STICKY_BAR' as const,
        placement: 'GLOBAL' as const,
        content: {
          title: design.title,
          subtitle: design.subtitle,
        },
        styles: {
          accentColor: design.accentColor,
          backgroundColor: design.backgroundColor,
          textColor: design.textColor,
          mutedColor: design.mutedColor,
          borderRadius: design.borderRadius,
          autoCloseMs: design.autoCloseMs,
          showCloseProgress: design.showCloseProgress,
          position: design.position,
          fontFamily: design.fontFamily,
          autoFitTheme: design.autoFitTheme,
          themeSchemeId: design.themeSchemeId,
        },
        layout: { position: design.position, sticky: true },
        animations: { enter: 'slide', durationMs: 220 },
        isEnabled: design.enabled,
      };

      if (widgetId) {
        await api.patch(`/widgets/${widgetId}`, payload);
      } else {
        const created = await api.post<{ id: string }>('/widgets', payload);
        setWidgetId(created.id);
      }
      await api
        .patch('/stores/setup', { widgetCustomized: true })
        .catch(() => undefined);
      markClean(design);
      setMessage(
        t("Cart Success Notification Bar saved. It shows when a free gift is added to cart."),
      );
      showAppToast(t("Cart Success Notification Bar saved"));
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Save failed';
      setError(msg);
      showAppToast(msg, { isError: true });
    } finally {
      setSaving(false);
    }
  }

  function discard() {
    const base = getBaseline();
    if (!base) return;
    setDesign(base);
    setError(null);
    setMessage(null);
    markCleanAfterDiscard(base);
  }

  const closeSecs = Math.round(design.autoCloseMs / 100) / 10;

  return (
    <Page
      title={t("Edit Cart Success Notification Bar")}
      subtitle={t("Shows when a free gift is locked into the cart")}
      backAction={{
        content: t("Back"),
        onAction: () => navigate(returnTo),
      }}
      primaryAction={
        !embedded
          ? {
              content: t("Save widget"),
              loading: saving,
              disabled: !dirty,
              onAction: () => void save(),
            }
          : undefined
      }
      secondaryActions={[
        {
          content: t("Reset defaults"),
          onAction: () => setDesign({ ...DEFAULT_CONGRATS_DESIGN }),
        },
      ]}
    >
      <ContextualSaveBar
        id="freebi-congrats-save"
        open={embedded && dirty && !loading}
        saving={saving}
        onSave={save}
        onDiscard={discard}
        saveLabel="Save"
      />
      <SavingOverlay
        open={saving}
        label={t("Saving Cart Success Notification Bar…")}
      />
      <BlockStack gap="400">
        {message && (
          <Banner tone="success" onDismiss={() => setMessage(null)}>
            {message}
          </Banner>
        )}
        {error && (
          <Banner tone="critical" onDismiss={() => setError(null)}>
            {error}
          </Banner>
        )}
        {loading && (
          <Banner tone="info">
            <p>{t("Loading Cart Success Notification Bar…")}</p>
          </Banner>
        )}

        <Banner tone="info">
          <p>
            {t("Default copy is")} <strong>{t("Gift Locked In 🎁 · Added to cart")}</strong>{t(".\n            Milestone / free-gift campaigns can override the message under\n            Display settings. Auto-close uses a countdown border so shoppers see\n            when the bar will disappear.")}
          </p>
        </Banner>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(280px, 380px) minmax(0, 1fr)',
            gap: 16,
            alignItems: 'start',
          }}
        >
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                {t("Content")}
              </Text>
              <FormLayout>
                <Checkbox
                  label={t("Enable Cart Success Notification Bar on storefront")}
                  checked={design.enabled}
                  onChange={(v) => patch('enabled', v)}
                />
                <TextField
                  label={t("Title (default)")}
                  value={design.title}
                  onChange={(v) => patch('title', v)}
                  autoComplete="off"
                  helpText={t("Campaigns can override this per offer")}
                />
                <TextField
                  label={t("Subtitle (default)")}
                  value={design.subtitle}
                  onChange={(v) => patch('subtitle', v)}
                  autoComplete="off"
                  helpText={t("e.g. \"Added to cart\"")}
                />
                <Select
                  label={t("Position")}
                  options={[
                    { label: t("Bottom of screen"), value: 'bottom' },
                    { label: t("Top of screen"), value: 'top' },
                  ]}
                  value={design.position}
                  onChange={(v) =>
                    patch('position', v === 'top' ? 'top' : 'bottom')
                  }
                />
              </FormLayout>

              <Text as="h2" variant="headingMd">
                {t("Auto-close")}
              </Text>
              <FormLayout>
                <RangeSlider
                  label={`Closes after ${closeSecs}s`}
                  value={design.autoCloseMs}
                  min={2000}
                  max={10000}
                  step={500}
                  onChange={(v) =>
                    patch('autoCloseMs', Array.isArray(v) ? v[0] : v)
                  }
                  output
                />
                <Checkbox
                  label={t("Show countdown border")}
                  checked={design.showCloseProgress}
                  onChange={(v) => patch('showCloseProgress', v)}
                  helpText={t("Progress line shrinks until the bar closes")}
                />
              </FormLayout>

              <Text as="h2" variant="headingMd">
                {t("Auto-fit with your theme")}
              </Text>
              <Banner tone="info">
                <p>
                  {t("When enabled, the storefront samples button and text colors\n                  from the live theme so the bar matches your brand.")}
                </p>
              </Banner>
              <Checkbox
                label={t("Auto-fit with your theme")}
                checked={design.autoFitTheme}
                onChange={(v) => patch('autoFitTheme', v)}
              />
              <ThemeColorRefresh
                onApply={(c) => {
                  setDesign((d) => ({
                    ...d,
                    autoFitTheme: true,
                    themeSchemeId: c.schemeId || d.themeSchemeId,
                    accentColor: c.accentColor || d.accentColor,
                    backgroundColor: c.backgroundColor || d.backgroundColor,
                    textColor: c.textColor || d.textColor,
                    mutedColor: c.mutedColor || d.mutedColor,
                  }));
                }}
              />

              <Text as="h2" variant="headingMd">
                {t("Style")}
              </Text>
              {design.autoFitTheme && (
                <Banner tone="warning">
                  <p>
                    {t("Manual colors are saved as fallbacks but won’t apply while\n                    Auto-fit is on.")}
                  </p>
                </Banner>
              )}
              <FormLayout>
                <ColorField
                  label={t("Accent / countdown")}
                  value={design.accentColor}
                  onChange={(v) => patch('accentColor', v)}
                  disabled={design.autoFitTheme}
                />
                <ColorField
                  label={t("Background")}
                  value={design.backgroundColor}
                  onChange={(v) => patch('backgroundColor', v)}
                  disabled={design.autoFitTheme}
                />
                <ColorField
                  label={t("Text color")}
                  value={design.textColor}
                  onChange={(v) => patch('textColor', v)}
                  disabled={design.autoFitTheme}
                />
                <ColorField
                  label={t("Muted text")}
                  value={design.mutedColor}
                  onChange={(v) => patch('mutedColor', v)}
                  disabled={design.autoFitTheme}
                />
                <RangeSlider
                  label={`Corner radius (${design.borderRadius}px)`}
                  value={design.borderRadius}
                  min={0}
                  max={28}
                  onChange={(v) =>
                    patch('borderRadius', Array.isArray(v) ? v[0] : v)
                  }
                  output
                />
              </FormLayout>

              {!embedded && (
                <Button
                  variant="primary"
                  loading={saving}
                  disabled={!dirty}
                  onClick={() => void save()}
                >
                  {t("Save widget")}
                </Button>
              )}
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="300">
              <InlineStack align="space-between" blockAlign="center">
                <Text as="h2" variant="headingMd">
                  {t("Live preview")}
                </Text>
                <ButtonGroup variant="segmented">
                  <Button
                    pressed={previewMode === 'desktop'}
                    onClick={() => setPreviewMode('desktop')}
                  >
                    {t("Desktop")}
                  </Button>
                  <Button
                    pressed={previewMode === 'mobile'}
                    onClick={() => setPreviewMode('mobile')}
                  >
                    {t("Mobile")}
                  </Button>
                </ButtonGroup>
              </InlineStack>
              <Text as="p" tone="subdued" variant="bodySm">
                {t("Countdown border previews auto-close (")}{closeSecs}{t("s).")}
              </Text>
              <div
                style={{
                  maxWidth: previewMode === 'mobile' ? 360 : '100%',
                  margin: '0 auto',
                }}
              >
                <CongratsPreview
                  design={design}
                  mode={previewMode}
                  progressPercent={previewProgress}
                />
              </div>
            </BlockStack>
          </Card>
        </div>
      </BlockStack>
    </Page>
  );
}
